const LEGACY_STOREFRONT_ORIGIN = 'https://www.luxuryclub.pe';

const PLACEHOLDER_LINKS = new Set(['', '#', 'a']);

const STATIC_ROUTE_ALIASES = new Map<string, string>([
  ['in', '/ingreso'],
  ['intro', '/inicio'],
  ['cart', '/checkout'],
  ['finance', '/financiamiento'],
  ['financy', '/financiamiento'],
  ['catalogo', '/productos'],
  ['catalogo-especial', '/especiales'],
  ['catalogo-experience', '/descuentos'],
  ['beneficios', '/descuentos'],
  ['luxury-pass', '/pases'],
]);

interface ParsedCmsLink {
  readonly path: string;
  readonly query: URLSearchParams;
  readonly originalAbsoluteHref: string | null;
}

/**
 * Adapts links stored in the shared Legacy CMS for the LC2 storefront without
 * mutating the source block consumed by Legacy web/mobile clients.
 */
export function resolveLegacyCmsLink(value: string | null | undefined): string | undefined {
  const raw = value?.trim() ?? '';
  if (PLACEHOLDER_LINKS.has(raw.toLowerCase())) return undefined;
  if (/^(?:mailto:|tel:)/i.test(raw)) return raw;
  if (/^[a-z][a-z\d+.-]*:/i.test(raw) && !/^(?:https?:|luxuryclub:)/i.test(raw)) {
    return undefined;
  }

  const parsed = parseCmsLink(raw);
  if (!parsed) return raw;

  const translated = translateLegacyPath(parsed.path, parsed.query);
  if (translated) return translated;
  if (parsed.originalAbsoluteHref) return parsed.originalAbsoluteHref;

  const normalizedPath = normalizePath(parsed.path);
  if (isLegacyInternalPath(normalizedPath)) {
    return `${LEGACY_STOREFRONT_ORIGIN}/${normalizedPath}${querySuffix(parsed.query)}`;
  }
  return normalizedPath ? `/${normalizedPath}${querySuffix(parsed.query)}` : undefined;
}

/** Used by Angular redirect routes when a Legacy deep link reaches LC2 directly. */
export function legacyProductRedirect(params: Record<string, string>): string {
  const segments = ['principal', 'product', params['group'], params['category']];
  if (params['brand']) segments.push('marca', params['brand']);
  if (params['product']) segments.push('producto', params['product']);
  return (
    translateLegacyPath(segments.filter(Boolean).join('/'), new URLSearchParams()) ?? '/productos'
  );
}

function parseCmsLink(raw: string): ParsedCmsLink | null {
  const hashRoute = extractHashRoute(raw);
  if (hashRoute) return parseRelativeRoute(hashRoute, raw);

  if (/^luxuryclub:\/\//i.test(raw)) {
    const route = raw.replace(/^luxuryclub:\/\//i, '');
    return parseRelativeRoute(route, raw);
  }

  if (/^(?:https?:)?\/\//i.test(raw)) {
    try {
      const absolute = new URL(raw, LEGACY_STOREFRONT_ORIGIN);
      return {
        path: absolute.pathname,
        query: absolute.searchParams,
        originalAbsoluteHref: raw,
      };
    } catch {
      return null;
    }
  }

  return parseRelativeRoute(raw, null);
}

function parseRelativeRoute(route: string, originalAbsoluteHref: string | null): ParsedCmsLink {
  const normalized = route.startsWith('/') ? route : `/${route}`;
  const parsed = new URL(normalized, LEGACY_STOREFRONT_ORIGIN);
  return {
    path: parsed.pathname,
    query: parsed.searchParams,
    originalAbsoluteHref,
  };
}

function extractHashRoute(raw: string): string | null {
  const match = raw.match(/#(?:!)?(\/[^#]*)$/);
  return match?.[1] ?? null;
}

function translateLegacyPath(path: string, query: URLSearchParams): string | null {
  const normalized = normalizePath(path);
  const staticAlias = STATIC_ROUTE_ALIASES.get(normalized);
  if (staticAlias) return mergeQuery(staticAlias, query);
  if (normalized.startsWith('luxury-pass/')) {
    return mergeQuery(`/pases/${normalized.slice('luxury-pass/'.length)}`, query);
  }

  const segments = normalized.split('/').filter(Boolean);
  if (segments[0] === 'busqueda') return translateSearchRoute(segments, query);
  if (segments[0] === 'beneficios') return translateBenefitsRoute(segments, query);
  if (
    segments[0] === 'tienda' &&
    segments[2] === 'categoria' &&
    segments[4] === 'marca' &&
    segments[6] === 'producto'
  ) {
    const group = segment(segments[1]);
    const category = segment(segments[3]);
    const product = segment(segments[7]);
    if (group && category && product) {
      return mergeQuery(`/tienda/${group}/categoria/${category}/producto/${product}`, query);
    }
  }
  if (segments[0] === 'tienda' && segments[2] === 'camp') {
    return translateCampaignRoute(segments, query);
  }
  if (segments[0] === 'catalogo-especial') {
    const group = segment(segments[1]);
    return mergeQuery(
      group && !/^(?:especials|specials)$/i.test(group) ? `/especiales/${group}` : '/especiales',
      query,
    );
  }
  if (segments[0] === 'catalogo-experience') {
    const category = segment(segments[1]);
    return mergeQuery(category ? `/descuentos/${category}` : '/descuentos', query);
  }
  if (segments[0] === 'catalogo') {
    const group = segment(segments[1]);
    return mergeQuery(group ? `/productos/${group}` : '/productos', query);
  }
  if (segments[0] !== 'principal') return null;
  if (segments[1] === 'product') return translateProductRoute(segments, query);
  if (segments[1] === 'experience') return translateExperienceRoute(segments, query);
  return null;
}

function translateSearchRoute(segments: readonly string[], query: URLSearchParams): string | null {
  const legacyType = segments[1];
  const searchTerm = segments.slice(2).join('/');
  if (!searchTerm || !['product', 'experience', 'vale', 'voucher'].includes(legacyType)) {
    return null;
  }

  const targetQuery = new URLSearchParams(query);
  targetQuery.set('q', decodeSegment(searchTerm));
  targetQuery.set(
    'tipo',
    legacyType === 'product' ? 'productos' : legacyType === 'experience' ? 'descuentos' : 'vales',
  );
  return mergeQuery('/busqueda', targetQuery);
}

function translateBenefitsRoute(
  segments: readonly string[],
  query: URLSearchParams,
): string | null {
  const companyIndex = segments.indexOf('empresa');
  const benefitIndex = segments.indexOf('beneficio');
  const company = segment(companyIndex >= 0 ? segments[companyIndex + 1] : undefined);
  const benefit = segment(benefitIndex >= 0 ? segments[benefitIndex + 1] : undefined);
  const categoryIndex = segments.indexOf('categoria');
  const category = segment(categoryIndex >= 0 ? segments[categoryIndex + 1] : segments[1]);

  if (company && benefit) {
    return mergeQuery(`/descuentos/comercio/${company}/descuento/${benefit}`, query);
  }
  if (company) return mergeQuery(`/descuentos/comercio/${company}`, query);
  return category ? mergeQuery(`/descuentos/${category}`, query) : '/descuentos';
}

function translateCampaignRoute(
  segments: readonly string[],
  query: URLSearchParams,
): string | null {
  const group = segment(segments[1]);
  const campaign = segment(segments[3]);
  if (!group) return '/especiales';
  if (!campaign) return mergeQuery(`/especiales/${group}`, query);
  const targetQuery = new URLSearchParams(query);
  if (!targetQuery.has('category')) targetQuery.set('category', campaign);
  return mergeQuery(`/especiales/${group}`, targetQuery);
}

function translateProductRoute(segments: readonly string[], query: URLSearchParams): string | null {
  const group = segment(segments[2]);
  const category = segment(segments[3]);
  if (!group) return '/productos';

  const brandIndex = segments.indexOf('marca');
  const productIndex = segments.indexOf('producto');
  const brand = segment(brandIndex >= 0 ? segments[brandIndex + 1] : undefined);
  const product = segment(productIndex >= 0 ? segments[productIndex + 1] : undefined);
  const special = isSpecialCampaign(group, category);

  if (special && category && brand && product) {
    return mergeQuery(
      `/tienda/${group}/campania/${category}/marca/${brand}/producto/${product}`,
      query,
    );
  }
  if (special && category && brand) {
    return mergeQuery(`/tienda/${group}/campania/${category}/marca/${brand}`, query);
  }
  if (special && category) {
    const targetQuery = new URLSearchParams(query);
    if (!targetQuery.has('category')) targetQuery.set('category', category);
    return mergeQuery(`/especiales/${group}`, targetQuery);
  }
  if (special) return mergeQuery(`/especiales/${group}`, query);

  if (category && brand && product) {
    return mergeQuery(`/tienda/${group}/categoria/${category}/producto/${product}`, query);
  }
  if (category && brand) {
    return mergeQuery(`/tienda/${group}/categoria/${category}/marca/${brand}`, query);
  }
  if (category && product) {
    return mergeQuery(`/tienda/${group}/categoria/${category}/producto/${product}`, query);
  }
  if (category) return mergeQuery(`/productos/${group}/${category}`, query);
  return mergeQuery(`/productos/${group}`, query);
}

function translateExperienceRoute(
  segments: readonly string[],
  query: URLSearchParams,
): string | null {
  const group = segment(segments[2]);
  const category = segment(segments[3]);
  const companyIndex = segments.indexOf('empresa');
  const company = segment(companyIndex >= 0 ? segments[companyIndex + 1] : undefined);
  const benefit = segment(companyIndex >= 0 ? segments[companyIndex + 2] : undefined);

  if (company && benefit) {
    return mergeQuery(`/descuentos/comercio/${company}/descuento/${benefit}`, query);
  }
  if (company) return mergeQuery(`/descuentos/comercio/${company}`, query);
  if (category) return mergeQuery(`/descuentos/${category}`, query);
  if (group) return mergeQuery(`/descuentos/${group}`, query);
  return '/descuentos';
}

function isSpecialCampaign(group: string, category: string | null): boolean {
  return /especial/i.test(group) || /^camp_/i.test(category ?? '');
}

function isLegacyInternalPath(path: string): boolean {
  return path === 'principal' || path.startsWith('principal/');
}

function normalizePath(path: string): string {
  return path.replace(/^\/+|\/+$/g, '');
}

function segment(value: string | undefined): string | null {
  if (!value) return null;
  try {
    return encodeURIComponent(decodeURIComponent(value));
  } catch {
    return encodeURIComponent(value);
  }
}

function decodeSegment(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function mergeQuery(target: string, source: URLSearchParams): string {
  const targetUrl = new URL(target, LEGACY_STOREFRONT_ORIGIN);
  source.forEach((value, key) => {
    if (!targetUrl.searchParams.has(key)) targetUrl.searchParams.append(key, value);
  });
  return `${targetUrl.pathname}${targetUrl.search}`;
}

function querySuffix(query: URLSearchParams): string {
  const value = query.toString();
  return value ? `?${value}` : '';
}
